/**
 * The stored shape of a sheet, and the structural edits that reshape it.
 *
 * Storage is sparse. A ledger with 3,000 rows and six used columns holds
 * around 18,000 cells, not the 3,000 × 16,384 the grid can address, so cells
 * live in a keyed map and an empty cell costs nothing.
 *
 * Only the *raw input* is stored — `"=SUM(B1:B9)"`, not `1250`. Computed
 * values are derived on load by the engine. Storing both would create two
 * sources of truth that drift the first time a dependency changes without the
 * dependent being rewritten, and a ledger that disagrees with itself is worse
 * than one that recomputes.
 *
 * Styles are interned. Formatting a whole column gives 3,000 cells the same
 * appearance; holding one style object and 3,000 short ids keeps the document
 * small enough to send on every autosave.
 */
import { cellKey, columnLabel, parseKey } from "./refs.js";
import { namesCell, offsetFormula, relinkCell, relinkRow, rewriteFormula } from "./transform.js";
/** Every edge of a cell, resolved from both border fields. */
export function resolvedBorders(style) {
    const edges = {};
    for (const side of style.borders ?? []) {
        edges[side] = style.borderColor
            ? { weight: 'thin', color: style.borderColor }
            : { weight: 'thin' };
    }
    for (const [side, edge] of Object.entries(style.borderEdges ?? {})) {
        if (edge)
            edges[side] = edge;
    }
    return edges;
}
/**
 * The border a merged region draws around itself.
 *
 * Excel keeps a merged region's edges on the cells underneath it, not on the
 * origin: a three-row "VERIFIED" box carries left and right on all three
 * cells, the top on the first and the bottom on the last. The grid draws the
 * origin only, so reading its style alone produced a box with no bottom — and
 * a table whose outer rule was missing wherever a merge met it.
 *
 * So the edges are composed from the band of cells along each side, taking
 * the heaviest each band offers, which is what Excel itself renders.
 */
export function mergeBorders(merge, styleAt) {
    const composed = {};
    const consider = (side, col, row) => {
        const edge = resolvedBorders(styleAt(col, row))[side];
        if (!edge)
            return;
        const current = composed[side];
        const winner = heavierEdge(current, edge);
        if (winner)
            composed[side] = winner;
    };
    for (let col = merge.left; col <= merge.right; col += 1) {
        consider('top', col, merge.top);
        consider('bottom', col, merge.bottom);
    }
    for (let row = merge.top; row <= merge.bottom; row += 1) {
        consider('left', merge.left, row);
        consider('right', merge.right, row);
    }
    return composed;
}
/** Which of two edges on the same side wins. Heavier is more deliberate. */
const WEIGHT_RANK = {
    hair: 0,
    dotted: 1,
    dashed: 2,
    thin: 3,
    medium: 4,
    thick: 5,
    double: 6,
};
/**
 * Which of two borders meeting on the same edge is drawn.
 *
 * One rule, in one place, because two of them is one too many: the grid
 * carried its own copy that ranked a double border below a thick one while
 * this ranked it above, so a merge and the cell beside it could disagree
 * about which of the two the shared edge was. Double sits at the top —
 * it is the heaviest thing a sheet can ask for, and it is what a total
 * is ruled off with.
 */
export function heavierEdge(a, b) {
    if (!a)
        return b;
    if (!b)
        return a;
    return WEIGHT_RANK[b.weight] > WEIGHT_RANK[a.weight] ? b : a;
}
/**
 * Every style property explicitly undefined.
 *
 * The style patcher reads `undefined` as "remove this property", so this is
 * what Clear formatting applies — and what the format painter merges the
 * copied style over, so the target ends up looking exactly like the source
 * rather than keeping whatever it had underneath.
 *
 * A function rather than a constant: it is spread into new objects and a
 * shared frozen literal invites somebody to mutate it.
 */
export function blankStyle() {
    return {
        fontFamily: undefined,
        fontSize: undefined,
        bold: undefined,
        italic: undefined,
        underline: undefined,
        strikethrough: undefined,
        color: undefined,
        fill: undefined,
        align: undefined,
        valign: undefined,
        wrap: undefined,
        indent: undefined,
        rotation: undefined,
        verticalText: undefined,
        shrinkToFit: undefined,
        textDirection: undefined,
        underlineStyle: undefined,
        script: undefined,
        fillPattern: undefined,
        fillPatternColor: undefined,
        format: undefined,
        decimals: undefined,
        currency: undefined,
        formatCode: undefined,
        numberPattern: undefined,
        negatives: undefined,
        negativeRed: undefined,
        zeroDash: undefined,
        borders: undefined,
        borderColor: undefined,
        borderEdges: undefined,
    };
}
/**
 * Pictures after rows or columns move.
 *
 * Below or right of the change, a picture moves with its cell. Inside a
 * deleted band it is not thrown away with the cells — a logo is not data, and
 * losing one to a row deletion would be a surprise nobody could undo from the
 * picture's side — it lands on the first line after the gap instead.
 */
export function shiftImages(images, spec) {
    if (!images?.length)
        return images;
    return images.map((image) => {
        const at = spec.axis === 'row' ? image.row : image.col;
        if (at < spec.at)
            return image;
        const removedTo = spec.at - spec.delta;
        const moved = spec.delta < 0 && at < removedTo ? spec.at : at + spec.delta;
        return spec.axis === 'row'
            ? { ...image, row: Math.max(0, moved), ...(moved !== at + spec.delta ? { y: 0 } : {}) }
            : { ...image, col: Math.max(0, moved), ...(moved !== at + spec.delta ? { x: 0 } : {}) };
    });
}
export const DEFAULT_COLUMN_WIDTH = 104;
export const DEFAULT_ROW_HEIGHT = 24;
/**
 * A new sheet's addressable size.
 *
 * A thousand rows because the grid is virtualised — only what is on screen
 * exists in the DOM — so the cost of an unused row is one entry in a prefix-sum
 * array, not a rendered element. Two hundred meant running out of sheet on an
 * ordinary year's ledger and having to insert rows to keep going.
 */
export const DEFAULT_ROWS = 1000;
export const DEFAULT_COLUMNS = 26;
// Excel's own defaults, so a workbook imported from his machine and a sheet
// started here look the same rather than subtly different.
export const DEFAULT_FONT_FAMILY = 'Calibri';
export const DEFAULT_FONT_SIZE = 11;
/**
 * Font choices, grouped the way Excel groups them.
 *
 * Every family here is either bundled with Windows or has a close metric
 * substitute on other platforms, so a sheet does not reflow when it is opened
 * somewhere else. Exotic display faces are deliberately absent: this is a
 * ledger, and a font nobody else has is a document nobody else can read.
 */
export const FONT_GROUPS = [
    {
        label: 'Sans serif',
        families: [
            'Calibri',
            'Arial',
            'Arial Black',
            'Segoe UI',
            'Tahoma',
            'Trebuchet MS',
            'Verdana',
            'Candara',
            'Corbel',
            'Franklin Gothic Medium',
            'Century Gothic',
        ],
    },
    {
        label: 'Serif',
        families: [
            'Cambria',
            'Times New Roman',
            'Georgia',
            'Garamond',
            'Book Antiqua',
            'Bookman Old Style',
            'Constantia',
            'Palatino Linotype',
            'Rockwell',
        ],
    },
    {
        label: 'Monospace',
        families: ['Consolas', 'Courier New', 'Lucida Console'],
    },
];
export const FONT_FAMILIES = FONT_GROUPS.flatMap((group) => group.families);
export const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72];
export function emptySheet(name) {
    return {
        name,
        cells: {},
        styles: {},
        columnWidths: {},
        rowHeights: {},
        merges: [],
        rowCount: DEFAULT_ROWS,
        columnCount: DEFAULT_COLUMNS,
        frozenRows: 0,
        frozenColumns: 0,
        tables: [],
    };
}
// ---------------------------------------------------------------------------
// Style interning
// ---------------------------------------------------------------------------
/**
 * A stable identity for a style object, so two cells formatted the same way
 * share one entry. Keys are sorted, which is what makes the identity depend on
 * the style's content rather than the order the properties happened to be set.
 */
export function styleSignature(style) {
    const entries = Object.entries(style)
        .filter(([, value]) => value !== undefined && value !== null)
        .sort(([a], [b]) => (a < b ? -1 : 1))
        .map(([key, value]) => `${key}:${describeValue(value)}`);
    return entries.join(';');
}
/**
 * One style value, as a string that is equal exactly when the values are.
 *
 * A border edge map is an object, and `String(value)` renders every object as
 * "[object Object]" — which would make two cells with entirely different
 * borders intern to the same style, and quietly repaint one of them.
 */
function describeValue(value) {
    if (Array.isArray(value))
        return [...value].sort().join('|');
    if (value && typeof value === 'object') {
        return Object.entries(value)
            .filter(([, inner]) => inner !== undefined && inner !== null)
            .sort(([a], [b]) => (a < b ? -1 : 1))
            .map(([key, inner]) => `${key}=${describeValue(inner)}`)
            .join(',');
    }
    return String(value);
}
export function isEmptyStyle(style) {
    return styleSignature(style) === '';
}
/**
 * Add a style to a table, reusing an existing id when an identical style is
 * already present. Returns the id and the (possibly unchanged) table.
 */
export function internStyle(styles, style) {
    if (isEmptyStyle(style))
        return { id: undefined, styles };
    const signature = styleSignature(style);
    for (const [id, existing] of Object.entries(styles)) {
        if (styleSignature(existing) === signature)
            return { id, styles };
    }
    // Ids are opaque and short; the sequence only has to avoid collisions.
    let next = Object.keys(styles).length;
    let id = `s${next}`;
    while (styles[id]) {
        next += 1;
        id = `s${next}`;
    }
    return { id, styles: { ...styles, [id]: style } };
}
/** Drop style entries no cell references any more. */
export function collectStyles(sheet) {
    const used = new Set();
    for (const cell of Object.values(sheet.cells)) {
        if (cell.style)
            used.add(cell.style);
    }
    const kept = {};
    for (const [id, style] of Object.entries(sheet.styles)) {
        if (used.has(id))
            kept[id] = style;
    }
    return kept;
}
export function resolveStyle(sheet, key) {
    const id = sheet.cells[key]?.style;
    return (id ? sheet.styles[id] : undefined) ?? {};
}
// ---------------------------------------------------------------------------
// Structural edits
// ---------------------------------------------------------------------------
function shiftIndexMap(map, spec) {
    const out = {};
    const removedTo = spec.at - spec.delta;
    for (const [rawIndex, size] of Object.entries(map)) {
        const index = Number(rawIndex);
        if (spec.delta < 0 && index >= spec.at && index < removedTo)
            continue;
        out[index < spec.at ? index : index + spec.delta] = size;
    }
    return out;
}
function shiftMerges(merges, spec) {
    const out = [];
    const removedTo = spec.at - spec.delta;
    for (const merge of merges) {
        const low = spec.axis === 'row' ? merge.top : merge.left;
        const high = spec.axis === 'row' ? merge.bottom : merge.right;
        // Entirely inside a deleted band: the merge goes with it.
        if (spec.delta < 0 && low >= spec.at && high < removedTo)
            continue;
        const move = (position) => {
            if (position < spec.at)
                return position;
            if (spec.delta < 0)
                return position < removedTo ? spec.at : position + spec.delta;
            return position + spec.delta;
        };
        const moved = spec.axis === 'row'
            ? { ...merge, top: move(merge.top), bottom: move(merge.bottom) }
            : { ...merge, left: move(merge.left), right: move(merge.right) };
        // A merge that collapsed to one cell is no longer a merge.
        if (moved.top === moved.bottom && moved.left === moved.right)
            continue;
        out.push(moved);
    }
    return out;
}
/**
 * Insert or delete rows or columns.
 *
 * Three things move together and all three must agree afterwards: the cells,
 * every formula that referenced them, and the layout (widths, heights,
 * merges). Doing any one of them without the others is what produces a sheet
 * that looks right and computes wrong.
 */
/**
 * Whether the band about to be removed is an unbroken run of chain links.
 *
 * This is the question that decides whether a repair is a repair or a
 * fabrication, and getting it wrong is not theoretical: an earlier version of
 * this asked only whether the reference pointed at the line immediately above,
 * which is also true of `=A1+B1` sitting in C1. Deleting column B would have
 * turned it into `=A1+A1` — a number that looks fine and counts A1 twice.
 *
 * So the removed cells are asked about themselves. Every one of them, from the
 * top of the band down, must be a formula naming the cell directly above it
 * along the axis being deleted. Where that holds, the band is a stretch of one
 * accumulator — each link standing on the one before — and the cell below it
 * can be joined to the cell above it, which is what removing links from a
 * chain means. Where it does not, nothing is touched and the reference becomes
 * `#REF!` as it always did.
 *
 * It also answers the head of the chain for free: `F11 = D11 - E11` names no
 * cell above it, so deleting the first entry row leaves `#REF!` rather than
 * inventing a balance to carry from a heading.
 */
function bandIsChain(sheet, spec, line, count) {
    for (let step = spec.at; step < spec.at + count; step += 1) {
        const key = spec.axis === 'row' ? cellKey(line, step) : cellKey(step, line);
        const cell = sheet.cells[key];
        if (!cell)
            return false;
        const above = spec.axis === 'row' ? { col: line, row: step - 1 } : { col: step - 1, row: line };
        if (!namesCell(cell.input, above, spec.axis))
            return false;
    }
    return true;
}
/**
 * Reconnect a running-balance chain over a deleted band.
 *
 * The delete-side twin of `relinkBelow`, and the answer to a real complaint:
 * deleting one transaction row turned the whole balance column below it into
 * `#REF!`, because `F13 = F12 + D13 - E13` names the cell that was deleted.
 * Excel does the same, which is precisely the fragility this system exists to
 * remove — see LEDGER-FORMAT §5.1.
 *
 * The rule is the narrowest one that fixes it, because sliding a broken
 * reference onto whatever took its place is exactly what `shiftRef` refuses
 * to do, and for good reason. All four of these must hold:
 *
 *   - the formula sits in the first surviving line below the band, so what it
 *     names is the line *immediately* above it;
 *   - the reference is in the formula's own column (or row, for a column
 *     delete) — "the balance above me", not a pointer at somebody else's data;
 *   - the reference is relative, since `$` is the user naming that line;
 *   - the line it would move to has something in it — see carriesForward.
 *
 * Under those four the reference means "the cell directly above me" before the
 * edit, and points at the cell directly above the formula's new position
 * after it. The relationship is preserved exactly, so nothing changes meaning
 * — which is the whole difference between this and sliding.
 *
 * Runs before the shift, so it rewrites a live reference rather than trying to
 * repair a `#REF!` that has already replaced one.
 */
function relinkOverDeletion(sheet, spec) {
    if (spec.delta >= 0 || spec.at === 0)
        return sheet;
    const count = -spec.delta;
    const lastRemoved = spec.at + count - 1;
    const survivor = spec.at + count;
    const target = spec.at - 1;
    const cells = { ...sheet.cells };
    let changed = false;
    for (const [key, cell] of Object.entries(sheet.cells)) {
        const position = parseKey(key);
        if (!position || !cell.input.startsWith('='))
            continue;
        const along = spec.axis === 'row' ? position.row : position.col;
        if (along !== survivor)
            continue;
        // The line this formula sits in, across the axis being deleted.
        const line = spec.axis === 'row' ? position.col : position.row;
        if (!bandIsChain(sheet, spec, line, count))
            continue;
        const from = spec.axis === 'row'
            ? { col: position.col, row: lastRemoved }
            : { col: lastRemoved, row: position.row };
        const to = spec.axis === 'row' ? { col: position.col, row: target } : { col: target, row: position.row };
        const relinked = relinkCell(cell.input, from, to, spec.axis);
        if (relinked !== cell.input) {
            cells[key] = { ...cell, input: relinked };
            changed = true;
        }
    }
    return changed ? { ...sheet, cells } : sheet;
}
export function applyShift(rawSheet, spec) {
    // Healing the chain is part of deleting, not a courtesy on top of it. The
    // service reshapes through this function directly, so a wrapper could be
    // bypassed and a ledger would then break or not depending on which door the
    // edit came in by.
    const sheet = relinkOverDeletion(rawSheet, spec);
    const cells = {};
    const removedTo = spec.at - spec.delta;
    for (const [key, cell] of Object.entries(sheet.cells)) {
        const position = parseKey(key);
        if (!position)
            continue;
        const axisPosition = spec.axis === 'row' ? position.row : position.col;
        // Cells in a deleted band are gone.
        if (spec.delta < 0 && axisPosition >= spec.at && axisPosition < removedTo)
            continue;
        const moved = axisPosition < spec.at ? axisPosition : axisPosition + spec.delta;
        const destination = spec.axis === 'row' ? cellKey(position.col, moved) : cellKey(moved, position.row);
        // Every surviving formula is rewritten, including ones that did not move:
        // a formula in row 1 can easily reference row 50.
        cells[destination] = { ...cell, input: rewriteFormula(cell.input, spec) };
    }
    return {
        ...sheet,
        cells,
        merges: shiftMerges(sheet.merges, spec),
        ...(sheet.images ? { images: shiftImages(sheet.images, spec) } : {}),
        /*
         * The rules over ranges move with their cells. They did not: inserting a
         * row in a ledger left its data bars and its drop-down lists on the rows
         * they used to be on, one row off from the figures they described.
         */
        ...(sheet.conditionalRules
            ? {
                conditionalRules: sheet.conditionalRules.flatMap((rule) => {
                    const ranges = shiftRanges(rule.ranges, spec);
                    return ranges.length > 0 ? [{ ...rule, ranges }] : [];
                }),
            }
            : {}),
        ...(sheet.validations
            ? {
                validations: sheet.validations.flatMap((rule) => {
                    const ranges = shiftRanges(rule.ranges, spec);
                    return ranges.length > 0 ? [{ ...rule, ranges }] : [];
                }),
            }
            : {}),
        columnWidths: spec.axis === 'column' ? shiftIndexMap(sheet.columnWidths, spec) : sheet.columnWidths,
        rowHeights: spec.axis === 'row' ? shiftIndexMap(sheet.rowHeights, spec) : sheet.rowHeights,
        rowCount: spec.axis === 'row' ? Math.max(1, sheet.rowCount + spec.delta) : sheet.rowCount,
        columnCount: spec.axis === 'column' ? Math.max(1, sheet.columnCount + spec.delta) : sheet.columnCount,
        frozenRows: spec.axis === 'row' && spec.at < sheet.frozenRows
            ? Math.max(0, sheet.frozenRows + spec.delta)
            : sheet.frozenRows,
        frozenColumns: spec.axis === 'column' && spec.at < sheet.frozenColumns
            ? Math.max(0, sheet.frozenColumns + spec.delta)
            : sheet.frozenColumns,
    };
}
/**
 * Copy formatting and formulas — never data — from the row above into freshly
 * inserted rows.
 *
 * Without this an inserted row is inert: it has no balance formula, so the
 * ledger shows a gap. Literal values are deliberately not copied, because
 * duplicating an amount into a new row would be inventing a transaction.
 */
function fillFromAbove(sheet, at, count) {
    if (at === 0)
        return sheet; // nothing above to inherit from
    const source = at - 1;
    const cells = { ...sheet.cells };
    for (const [key, cell] of Object.entries(sheet.cells)) {
        const position = parseKey(key);
        if (!position || position.row !== source)
            continue;
        for (let offset = 1; offset <= count; offset += 1) {
            const target = cellKey(position.col, source + offset);
            if (cells[target])
                continue; // the shift already put something here
            const isFormula = cell.input.startsWith('=');
            // Nothing to carry down means no cell at all — writing an empty,
            // unstyled entry would grow the document for no reason.
            if (!isFormula && !cell.style)
                continue;
            const input = isFormula ? offsetFormula(cell.input, 0, offset) : '';
            cells[target] = cell.style ? { input, style: cell.style } : { input };
        }
    }
    return { ...sheet, cells };
}
/**
 * Reconnect the row immediately below an insert.
 *
 * Only for columns where the insert actually inherited a formula. That
 * condition is the whole safety of this step: repointing a chain at a row that
 * carries nothing would drop everything accumulated above it, turning a
 * balance of 150 into 50. Relinking and inheriting are one operation, and
 * doing either alone makes the sheet wrong in a different direction.
 *
 * Runs after fillFromAbove for exactly that reason — it needs to see what the
 * fill produced.
 */
function relinkBelow(sheet, at, count) {
    if (at === 0)
        return sheet;
    const targetRow = at + count;
    const lastInserted = at + count - 1;
    const cells = { ...sheet.cells };
    for (const [key, cell] of Object.entries(sheet.cells)) {
        const position = parseKey(key);
        if (!position || position.row !== targetRow)
            continue;
        const carrier = sheet.cells[cellKey(position.col, lastInserted)];
        if (!carrier?.input.startsWith('='))
            continue;
        const relinked = relinkRow(cell.input, at - 1, lastInserted);
        if (relinked !== cell.input)
            cells[key] = { ...cell, input: relinked };
    }
    return { ...sheet, cells };
}
/**
 * Insert rows, keeping the sheet usable and its arithmetic honest.
 *
 * Three steps, and all three are needed. The shift moves cells and rewrites
 * references. The relink reconnects the row below so a balance chain does not
 * step over the gap. The fill gives the new rows the formatting and formulas
 * of the row above, so they work the moment they are typed into.
 */
export function insertRows(sheet, at, count = 1, inherit = true) {
    const shifted = applyShift(sheet, { axis: 'row', at, delta: count });
    if (!inherit)
        return shifted;
    return relinkBelow(fillFromAbove(shifted, at, count), at, count);
}
export function deleteRows(sheet, at, count = 1) {
    return applyShift(sheet, { axis: 'row', at, delta: -count });
}
export function insertColumns(sheet, at, count = 1) {
    return applyShift(sheet, { axis: 'column', at, delta: count });
}
export function deleteColumns(sheet, at, count = 1) {
    return applyShift(sheet, { axis: 'column', at, delta: -count });
}
// ---------------------------------------------------------------------------
// Bounds
// ---------------------------------------------------------------------------
/** The smallest box containing every populated cell. Null when empty. */
export function usedBounds(sheet) {
    let top = Infinity;
    let left = Infinity;
    let bottom = -1;
    let right = -1;
    for (const key of Object.keys(sheet.cells)) {
        const position = parseKey(key);
        if (!position)
            continue;
        if (position.row < top)
            top = position.row;
        if (position.row > bottom)
            bottom = position.row;
        if (position.col < left)
            left = position.col;
        if (position.col > right)
            right = position.col;
    }
    return bottom < 0 ? null : { top, left, bottom, right };
}
/**
 * The block of filled cells around one cell — Excel's "current region".
 *
 * What Ctrl+A finds from inside a table, and what every Data command uses when
 * only one cell is selected: sorting a ledger should not need the ledger to be
 * selected first, and asking the user to drag over four thousand rows before
 * pressing A→Z is not an answer.
 *
 * Grown outwards a row and a column at a time and stopped by an entirely empty
 * line, exactly as Excel does. That rule is what makes a blank row a divider
 * between two blocks — which is how people actually separate them — while a
 * gap in one column is just a gap.
 *
 * A cell with nothing around it is its own region, so a command acting on it
 * does nothing rather than reaching across the sheet.
 */
export function regionAround(sheet, col, row) {
    const filled = (c, r) => {
        const cell = sheet.cells[cellKey(c, r)];
        return cell !== undefined && cell.input !== '';
    };
    const rowHasSomething = (r, left, right) => {
        for (let c = left; c <= right; c += 1)
            if (filled(c, r))
                return true;
        return false;
    };
    const columnHasSomething = (c, top, bottom) => {
        for (let r = top; r <= bottom; r += 1)
            if (filled(c, r))
                return true;
        return false;
    };
    let top = row;
    let bottom = row;
    let left = col;
    let right = col;
    /*
     * Alternating passes rather than one sweep each way. Widening can reveal a
     * filled cell in a row that looked empty, and lengthening can reveal one in
     * a column that did — a block whose first row is a merged title over columns
     * further right than the cell you started in is the ordinary case.
     */
    let grew = true;
    while (grew) {
        grew = false;
        while (top > 0 && rowHasSomething(top - 1, left, right)) {
            top -= 1;
            grew = true;
        }
        while (bottom < sheet.rowCount - 1 && rowHasSomething(bottom + 1, left, right)) {
            bottom += 1;
            grew = true;
        }
        while (left > 0 && columnHasSomething(left - 1, top, bottom)) {
            left -= 1;
            grew = true;
        }
        while (right < sheet.columnCount - 1 && columnHasSomething(right + 1, top, bottom)) {
            right += 1;
            grew = true;
        }
    }
    return { top, left, bottom, right };
}
/** Raw inputs as the engine wants them. */
export function toRawCells(sheet) {
    const raw = new Map();
    for (const [key, cell] of Object.entries(sheet.cells)) {
        if (cell.input !== '')
            raw.set(key, cell.input);
    }
    return raw;
}
/** Human-readable range label, e.g. `A1:D12`, for the status bar. */
export function describeBounds(bounds) {
    return `${columnLabel(bounds.left)}${bounds.top + 1}:${columnLabel(bounds.right)}${bounds.bottom + 1}`;
}
/**
 * Ranges after rows or columns move.
 *
 * Below or right of the change, a range moves. One the change falls inside
 * grows or shrinks with it. One wholly inside a deleted band goes. A range
 * whose far end was deleted now ends on the last line before the gap — not
 * on the first line after it, which is where the rows below closed up to.
 */
export function shiftRanges(ranges, spec) {
    const removedTo = spec.at - spec.delta;
    const out = [];
    for (const range of ranges) {
        const low = spec.axis === 'row' ? range.top : range.left;
        const high = spec.axis === 'row' ? range.bottom : range.right;
        let from = low;
        let to = high;
        if (spec.delta > 0) {
            if (low >= spec.at)
                from = low + spec.delta;
            if (high >= spec.at)
                to = high + spec.delta;
        }
        else {
            if (low >= spec.at && high < removedTo)
                continue;
            from = low < spec.at ? low : low < removedTo ? spec.at : low + spec.delta;
            to = high < spec.at ? high : high < removedTo ? spec.at - 1 : high + spec.delta;
            if (to < from)
                continue;
        }
        out.push(spec.axis === 'row'
            ? { ...range, top: from, bottom: to }
            : { ...range, left: from, right: to });
    }
    return coalesceRanges(out);
}
/**
 * The same cells in as few rectangles as possible.
 *
 * Excel fragments a rule's ranges every time rows are copied, inserted or
 * filled — "I5:I6 I8 I9:I40 I41" — and a ledger kept for years arrives with
 * hundreds of them for what is one column. Contained ranges are dropped, and
 * two that sit edge to edge with the same extent the other way are joined,
 * until nothing more joins.
 */
/**
 * Ranges on one conditional-formatting or validation rule.
 *
 * One number, used by the save check and by the import alike. It was 64 on
 * the save side and unlimited on the import side, so a ledger whose rules
 * Excel had fragmented into more than 64 pieces imported cleanly — and then
 * every autosave of that tab was refused, and every edit on it lost.
 * Coalesced first (see coalesceRanges), real rules come nowhere near this.
 */
export const MAX_RULE_RANGES = 2048;
export function coalesceRanges(ranges) {
    let list = ranges.map((range) => ({ ...range }));
    let changed = true;
    while (changed) {
        changed = false;
        // Contained in another: gone.
        list = list.filter((range, index) => !list.some((other, at) => at !== index &&
            other.top <= range.top &&
            other.bottom >= range.bottom &&
            other.left <= range.left &&
            other.right >= range.right &&
            // Of two identical ranges, keep the first.
            (at < index ||
                other.top !== range.top ||
                other.bottom !== range.bottom ||
                other.left !== range.left ||
                other.right !== range.right)));
        // Edge to edge with the same span: one range.
        list.sort((a, b) => a.left - b.left || a.top - b.top);
        for (let i = 0; i < list.length; i += 1) {
            for (let j = i + 1; j < list.length; j += 1) {
                const a = list[i];
                const b = list[j];
                const stacked = a.left === b.left &&
                    a.right === b.right &&
                    b.top <= a.bottom + 1 &&
                    a.top <= b.bottom + 1;
                const beside = a.top === b.top &&
                    a.bottom === b.bottom &&
                    b.left <= a.right + 1 &&
                    a.left <= b.right + 1;
                if (stacked || beside) {
                    list[i] = {
                        top: Math.min(a.top, b.top),
                        bottom: Math.max(a.bottom, b.bottom),
                        left: Math.min(a.left, b.left),
                        right: Math.max(a.right, b.right),
                    };
                    list.splice(j, 1);
                    changed = true;
                    // Keep growing this one: look again at everything after it.
                    j = i;
                }
            }
        }
    }
    return list;
}
//# sourceMappingURL=document.js.map